import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Button, MenuItem, TextField, Tooltip, Typography} from "@mui/material";
import {FileDownloadOutlined, HelpOutlineOutlined, MenuBook,
        TableChartOutlined} from "@mui/icons-material";
import {IconButton} from "@mui/material";
import {openGuide} from "../util/guideWindow";

import PageHeader from "../components/Common/PageHeader";
import ReportResultTableApplet from "../eoec/applet/impl/Reporting/ReportResultTableApplet";
import ReportShelves from "../components/Reporting/ReportShelves";
import css_buttons from "../components/Common/css/CCButton.module.scss";
import {makeEnterpriseComponent} from "../util/utility";
import {Blue3, Grey5, Grey6, Grey17} from "../theme/styles";
import {Dataset, Note, getReportDatasets} from "../components/Reporting/reportApi";
import {Shelves, buildQuery, refusals} from "../components/Reporting/buildQuery";
import {SHAPES} from "../components/Reporting/querySpec";
import {csvFilename, downloadCsv, resultToCsv} from "../components/Reporting/resultCsv";
import {saveExported} from "../components/Reporting/resultExcel";
import FieldRoleHelp from "../components/Reporting/FieldRoleHelp";
import ReportChart from "../components/Reporting/ReportChart";
import SavedReportBar from "../components/Reporting/SavedReportBar";
import ScheduleBar from "../components/Reporting/ScheduleBar";
import PersonaScope from "../components/Reporting/PersonaScope";
import PersonaHeaderInfo from "../components/Common/PersonaHeaderInfo";
import ExplainPanel from "../components/Reporting/ExplainPanel";
import EventBus from "../util/EventBus";
import ChartBuilder from "../components/Reporting/ChartBuilder";
import {ChartSpec, availableCharts, defaultSpec} from "../components/Reporting/chartSpec";
import {ExportFormat, ReportRun, SavedReport, exportRun, getRun, getRuns, getSavedReports,
        runAndRecord,
        saveReport} from "../components/Reporting/reportStoreApi";
import {BUILDER_COLUMN, BUILDER_SECOND_COLUMN} from "../components/Reporting/builderLayout";
import {groupNotes, noteText} from "../components/Reporting/datasetNotes";

/**
 * The report builder. UNLINKED ON PURPOSE -- see components/Reporting/reportingBoundary.test.ts.
 *
 * Reachable at /reporting and offered by nothing: not the rail, not the app switcher, not global
 * search. It is being built inside t5 because the compiler, the projection and the reference data it
 * needs are here, and it moves to t10 when it is ready.
 *
 * NO SQL IS WRITTEN OR SENT. The page arranges shelves, buildQuery turns them into a specification,
 * and the server compiles it against a catalogue it built for this session's realm. The realm is not
 * a field on this page and cannot be: a specification that mentions one is refused by the service.
 *
 * IT IMPORTS ONLY WHAT EVERY TIER HAS -- PageHeader, the theme, the EOEC framework, MUI. Nothing
 * from Catalog/ or DataQuality/, however convenient. That is the rule that makes the move a move.
 */

/** Whether every column a chart names is still in this result. */
function fits(spec: ChartSpec, columns: {Key: string}[]): boolean {
    const keys = new Set(columns.map(one => one.Key));
    return Object.values(spec.encodings ?? {})
        .every(held => (held ?? []).every(key => keys.has(key)));
}

const EMPTY: Shelves = {dataset: "", rows: [], values: [], filters: []};

/** How tall the drawing may get. Big enough to read on a laptop, small enough that a tall monitor
 *  does not turn three bars into a poster. */
const CHART_MAX_HEIGHT = 420;

/** And the floor. Below this a chart stops being readable, and a short window is better served by
 *  a small chart the reader scrolls to than by one squeezed into nothing. */
const CHART_MIN_HEIGHT = 260;

export default function ReportBuilder() {
    const [datasets, setDatasets] = useState<Dataset[]>([]);
    const [notes, setNotes] = useState<Note[]>([]);
    const [shelves, setShelves] = useState<Shelves>(EMPTY);
    const [failure, setFailure] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [ran, setRan] = useState(false);

    const [saved, setSaved] = useState<SavedReport[]>([]);
    const [current, setCurrent] = useState<SavedReport | null>(null);
    const [name, setName] = useState("");
    const [runs, setRuns] = useState<ReportRun[]>([]);
    // What was on the shelves when this report was last saved or opened, so "unsaved changes" is a
    // comparison rather than a flag somebody has to remember to set.
    const [savedShelves, setSavedShelves] = useState<string>("");

    // WHAT THE RESULT IS SHOWN AS. The rows come back from the same run either way -- switching to
    // a chart never re-queries, because a chart that disagreed with the table above it would be the
    // worst possible outcome of offering both.
    const [view, setView] = useState<"table" | "query" | "chart">("table");

    // THE DRAWING SITS BELOW EVERYTHING THAT BUILT IT -- the field list, the shelves, the filters,
    // the chart strip and its options. On an ordinary window that is some seven hundred pixels of
    // scrolling between pressing Chart and seeing anything, so the chart appeared not to have
    // drawn at all. Reported by Ed, 2026-09-30, watching a radar being made: "the chart is
    // clipping on the bottom, I am having to scroll".
    const chartRef = React.useRef<HTMLDivElement | null>(null);

    // THE SCROLLING AREA, measured rather than assumed. "Make the chart fill the height" cannot be
    // done in CSS here: MUI v9 charts take a NUMBER, and a percentage string draws nothing at all
    // and says nothing about it -- the note in ReportChart says so and this is where it bites.
    // So the number comes from the box the chart is scrolled into.
    const scrollerRef = React.useRef<HTMLDivElement | null>(null);
    const [chartHeight, setChartHeight] = React.useState(340);
    // THE CHART BEING BUILT. Null until somebody opens the Chart view, and reset whenever the
    // COLUMNS change -- a specification names columns by key, and last report's keys mean nothing
    // to this one. Kept on the page rather than in the chart, because the builder and the drawing
    // are two views of one document.
    const [chart, setChart] = useState<ChartSpec | null>(null);
    // WHAT THE STORED CHART LOOKS LIKE, so "Save report" can say there is something to save. The
    // shelves already work this way; a chart somebody built and did not save is the same kind of
    // unsaved work and should read as such rather than silently not persisting.
    const [savedChart, setSavedChart] = useState<string | null>(null);
    // Bumped whenever the persona changes, and used to remount the query panel so it asks again.
    const [personaEpoch, setPersonaEpoch] = useState(0);
    const [shapeHelp, setShapeHelp] = useState(false);
    // WHEN THESE ROWS ARE FROM. A run opened from the history or an email is
    // evidence of a past moment, and a file named for today would misdate it.
    const [openedRunAt, setOpenedRunAt] = useState<string | null>(null);
    // WHICH RECORDED RUN THESE ROWS ARE. The CSV is built from what the page is holding, so it needs
    // nothing; the spreadsheet is built by the server FROM THE RECORDED RUN, so without this there
    // is nothing to ask for. Null before a run, and null again if recording failed -- which is the
    // case where the rows on screen are right and there is no stored run to export.
    const [runUSID, setRunUSID] = useState<string | null>(null);
    // A ROUND TRIP, unlike the CSV, so the control says so rather than looking ignored. WHICH
    // format is building, not merely that one is: with two buttons, a shared boolean puts
    // "Building…" on both and the reader cannot tell which one they pressed.
    const [exporting, setExporting] = useState<ExportFormat | null>(null);
    const [result, setResult] = useState<{Columns: any[]; Rows: unknown[][]; RecordKeys?: string[]}>(
        {Columns: [], Rows: []});

    // Created once. A component rebuilt per run means a grid REMOUNTED per run, which throws away
    // the reader's column widths and scroll position every time they change an aggregation.
    //
    // makeEnterpriseComponent, NOT enterpriseComponentClassFactory. The factory returns the CLASS;
    // this constructs it. Written the other way first, with a `?? object` fallback that quietly
    // handed PlatformTable the class itself -- so the grid threw
    // "getEnterpriseComponent(...).getName is not a function" the moment anybody pressed Run, and
    // the result grid had never once rendered. Nothing caught it: tsc is happy with `any`, the unit
    // tests do not mount the page, and I could not sign in to look. The Playwright suite found it
    // on its first run.
    const [component] = useState<any>(() => makeEnterpriseComponent("ReportResult"));

    const loadSaved = useCallback(() => {
        getSavedReports().then(setSaved).catch(() => setSaved([]));
    }, []);

    useEffect(() => { loadSaved(); }, [loadSaved]);

    const loadDatasets = useCallback(() => {
        getReportDatasets()
            .then(answer => { setDatasets(answer.Datasets); setNotes(answer.Notes); })
            .catch(() => setFailure("Could not read what this account can report on."));
    }, []);

    useEffect(() => { loadDatasets(); }, [loadDatasets]);

    // A RESULT BELONGS TO THE PERSONA IT WAS COMPILED UNDER, so changing the persona discards it.
    //
    // THE ALTERNATIVE IS THE DANGEROUS ONE. Rows computed as an Organization, still on screen after
    // switching to Position, are a wider answer wearing a narrower label -- and nothing about them
    // looks stale. Clearing is the only honest state: the reader presses Run and gets an answer
    // that matches the scope now displayed beside it.
    //
    // THE CATALOGUE RELOADS TOO. It is built per caller and the request is persona-checked, so it
    // is not safe to assume the datasets a Position may report on are the ones an Organization may.
    useEffect(() => {
        const onChangeVisibility = () => {
            setRan(false);
            setFailure(null);
            // AND THE STATEMENT IS ASKED FOR AGAIN. The panel re-reads when the SPECIFICATION
            // changes, and a persona change does not touch it -- so without this the statement on
            // screen was compiled for the persona somebody just switched away from. Today the two
            // read identically, because the persona predicate is always the same sentence once it
            // is taken out; that is a property of the redaction, not a reason to show stale text.
            setPersonaEpoch(one => one + 1);
            component?.setQuery?.(null);
            component?.invalidate?.();
            loadSaved();
            loadDatasets();
        };
        EventBus.visibility.addListener("change-visibility", onChangeVisibility);
        return () => { EventBus.visibility.removeListener("change-visibility", onChangeVisibility); };
    }, [component, loadDatasets, loadSaved]);

    const dataset = useMemo(
        () => datasets.find(one => one.Name === shelves.dataset), [datasets, shelves.dataset]);

    // WHY THE BUTTON IS OFF, not merely that it is. A disabled control with no reason is the thing
    // people file tickets about.
    const wrong = useMemo(() => refusals(shelves, dataset), [shelves, dataset]);

    // BUILT ONCE. The tab row asks whether there is a query to show and the panel shows it; two
    // calls would be two objects, and the panel re-reads whenever its query changes identity.
    const query = useMemo(() => buildQuery(shelves, dataset), [shelves, dataset]);

    const run = useCallback(() => {
        const query = buildQuery(shelves, dataset);
        if (!query) return;
        setBusy(true);
        setFailure(null);
        // CLEARED BEFORE THE ROWS CHANGE. Left set, the Excel button would fetch the PREVIOUS run
        // while the new one's rows were on screen -- a file that disagrees with the page it was
        // downloaded from, and nothing about it would look wrong.
        setRunUSID(null);
        component?.setQuery?.(query);
        component?.newQuery?.();
        component?.invalidate?.();
        // The grid re-queries from its own invalid listener; this page only says WHEN.
        setRan(true);
        setBusy(false);
        // PRESSING RUN MEANS "SHOW ME THE ROWS". Somebody reading the Query tab who runs the report
        // is asking what it returns, and leaving them on the statement they were already looking at
        // answers a question they did not ask.
        if (view === "query") setView("table");

        // AND KEEP WHAT IT SAID. Recorded whether or not the report is saved: running it is how
        // somebody arrives at one worth keeping, and those rows are evidence of what the data said
        // at that moment either way. It runs a second time server-side to do it, which is the
        // honest cost of the grid owning its own fetch -- and it is what makes the STORED rows the
        // ones the store vouches for rather than a copy of what a browser happened to render.
        runAndRecord(query, current?.USID)
            .then((answer: any) => {
                // THE SAME ROWS THE STORE KEPT, which is what the chart draws. Reading them from
                // the recorded run rather than from the grid's buffer means the picture and the
                // evidence are the same numbers by construction, not by coincidence.
                setResult({Columns: answer?.Columns ?? [], Rows: answer?.Rows ?? [],
                           RecordKeys: answer?.RecordKeys});
                // THE CHART IS ABOUT THESE COLUMNS -- but only drop it when it no longer FITS.
                // Re-running the same report returns the same columns, and clearing it there would
                // throw away a chart somebody saved every time they pressed Run. So: keep it while
                // every column it names is still in the result, and re-default when one is gone.
                setChart(previous => previous && fits(previous, answer?.Columns ?? [])
                    ? previous : null);
                setOpenedRunAt(null);
                setRunUSID(answer?.RunUSID ?? null);
                if (current) getRuns(current.USID).then(setRuns).catch(() => undefined);
            })
            // NOT SWALLOWED. This used to be `.catch(() => undefined)`, and it hid a 500 for an
            // afternoon: the grid fetches its own rows, so a failure here left a table full of
            // data under a heading that read "No columns" -- which looks like a rendering bug and
            // is not one. The cause was a decorator on the wrong line, and only a CloudWatch
            // traceback said so.
            //
            // AND IT NO LONGER CLAIMS THE ROWS ARE BELOW. This message used to read "The rows below
            // are correct; the chart and saved history may not be", written when the grid fetched
            // its own rows and a failure here left them on screen. It does not any more -- the
            // result comes back WITH the recorded run, so a failure here leaves the result area
            // reading "No columns". A message promising rows that are not there sends the reader
            // looking for a rendering fault instead of pressing Run again. Found by an e2e test
            // that asserted the old sentence and could not find the rows it named.
            .catch(() => {
                setRunUSID(null);
                setFailure("The report ran, but its result could not be recorded, so there is "
                          + "nothing to show. Press Run to try again.");
            });
    }, [component, current, dataset, shelves, view]);

    const openSaved = useCallback((report: SavedReport | null) => {
        setCurrent(report);
        setRan(false);
        setRuns([]);
        setRunUSID(null);
        if (!report) {
            setName("");
            setShelves(EMPTY);
            setSavedShelves("");
            return;
        }
        setName(report.Name);
        // ITS OWN CHART, if it has one. A report saved before charts were stored has none and falls
        // back to the default for its result, which is exactly what it always drew.
        setChart(report.ChartSpecification ?? null);
        setSavedChart(report.ChartSpecification
            ? JSON.stringify(report.ChartSpecification) : null);
        // BACK ONTO THE SHELVES. A saved specification is the same document buildQuery produces, so
        // opening one is reading its columns back into the arrangement that made them.
        const spec: any = report.Specification ?? {};
        const next: Shelves = {
            dataset: spec.dataset ?? "",
            rows: (spec.columns ?? []).filter((one: any) => one.role === "dimension")
                .map((one: any) => ({field: one.of?.field, grain: one.grain})),
            values: (spec.columns ?? []).filter((one: any) => one.role === "measure")
                .map((one: any) => ({field: one.of?.field, aggregation: one.aggregation})),
            filters: spec.filters ?? [],
            sort: spec.sort,
            limit: spec.limit,
        };
        setShelves(next);
        setSavedShelves(JSON.stringify(next));
        getRuns(report.USID).then(setRuns).catch(() => setRuns([]));
    }, []);

    const keep = useCallback(() => {
        const query = buildQuery(shelves, dataset);
        if (!query) { setFailure("Finish the report before saving it."); return; }
        setBusy(true);
        setFailure(null);
        // THE CHART GOES WITH IT. `chart` is null until somebody opens the Chart view, and null
        // means "say nothing about it" all the way to the database -- so saving a report you have
        // not drawn does not erase a chart you drew yesterday.
        saveReport({USID: current?.USID, Name: name.trim(), Specification: query,
                    ChartSpecification: chart})
            .then(answer => {
                setSavedShelves(JSON.stringify(shelves));
                setSavedChart(chart ? JSON.stringify(chart) : null);
                loadSaved();
                if (!current) setCurrent({USID: answer.USID, Name: name.trim(), Specification: query,
                                          ChartSpecification: chart});
            })
            .catch((error: any) => setFailure(
                error?.response?.data?.Cause ?? "Could not save that report."))
            .finally(() => setBusy(false));
    }, [chart, current, dataset, loadSaved, name, shelves]);

    // WHAT A PAST RUN SAID, shown as it was rather than re-run. The grid renders the stored rows;
    // nothing is recomputed, which is the entire point of having kept them.
    const openRun = useCallback((run: ReportRun) => {
        getRun(run.USID).then(stored => {
            // NO RECORD KEYS, DELIBERATELY. A recorded run is evidence of what the data said at
            // that moment; a link out of it would open the record as it is NOW, which is a
            // different thing wearing the same row. So a reopened run shows its rows and offers no
            // way in -- and the store does not grow a UUID per row to make it possible.
            setResult({Columns: stored?.Columns ?? [], Rows: stored?.Rows ?? []});
            setOpenedRunAt(String(stored?.RunDatetime ?? "") || null);
            setRunUSID(String(stored?.USID ?? "") || null);
            component?.setStoredResult?.(stored);
            component?.newQuery?.();
            component?.invalidate?.();
            setRan(true);
        }).catch(() => setFailure("Could not read that run."));
    }, [component]);

    // OPENED BY URL, because that is where a scheduled run's email points. The link names the RUN
    // rather than the report on purpose: opening the report would re-run it, under whatever the
    // reader's persona is at that moment, and show a different answer from the one the email was
    // about.
    //
    // THE ROWS ARRIVE WITHOUT THE SHELVES, and that is honest rather than unfinished: a run is
    // evidence of what the data said, and the builder above it is a fresh question. Loading the
    // specification back onto the shelves would suggest the two are the same thing.
    const [openedFromLink, setOpenedFromLink] = useState(false);
    useEffect(() => {
        if (openedFromLink) return;
        const wanted = new URLSearchParams(window.location.search).get("run");
        if (!wanted) return;
        setOpenedFromLink(true);
        getRun(wanted)
            .then(stored => {
                setResult({Columns: stored?.Columns ?? [], Rows: stored?.Rows ?? []});
                setOpenedRunAt(String(stored?.RunDatetime ?? "") || null);
                // THE THIRD WAY A RUN GETS ON SCREEN, and the one that matters most for the
                // workbook: this is where a scheduled report's email lands. Missing here, the
                // Excel button was offered to somebody who had just pressed Run and withheld from
                // the reader who arrived from the notification -- the audience the file is for.
                // Three paths set the result and only two set this; found by opening a real
                // scheduled run in the browser, not by any test.
                setRunUSID(String(stored?.USID ?? "") || null);
                component?.setStoredResult?.(stored);
                component?.newQuery?.();
                component?.invalidate?.();
                setRan(true);
            })
            // THE SAME REFUSAL A RUN OPENED FROM THE HISTORY WOULD GIVE. A link from an email is
            // the one place somebody arrives with no context at all, so "could not read that run"
            // has to be a sentence rather than an empty page.
            .catch(() => setFailure("Could not open that run. It may have been removed, or belong "
                                    + "to an account you are not signed in to."));
    }, [component, openedFromLink]);

    // The notes for the chosen dataset only. All of them at once is a wall nobody reads.
    const relevant = notes.filter(one => one.dataset === shelves.dataset);

    // THE VIEWS THIS REPORT CURRENTLY HAS. Table and the charts need rows, so they appear once it
    // has run; Query needs only a specification that compiles, so it is there from the moment the
    // shelves say something -- which is when somebody wondering what the report will ask is most
    // likely to want it.
    // ONE "Chart" VIEW, NOT ONE PER KIND. The type is a choice INSIDE the chart now -- picking it
    // is the first thing the builder asks -- so three tabs that each meant "and also switch type"
    // became one tab that means "draw this". The tab is refused only when NO chart could be drawn,
    // and then it carries the reason the simplest chart gave, which is the most useful one: a
    // result with nothing summarized cannot be drawn as anything.
    // FILLS THE VIEW, AND RE-FILLS IT WHEN THE WINDOW CHANGES. A fixed 340 was a third of an
    // ordinary window: the drawing was the smallest thing on a page entirely about it.
    //
    // LESS THE BREATHING ROOM THE SCROLL MARGIN RESERVES, so the chart that is scrolled to still
    // ends short of the bottom edge rather than on it -- the two have to agree or the chart grows
    // into exactly the clipped look the margin exists to prevent.
    React.useEffect(() => {
        // MEASURED FROM WHERE THE CHART ACTUALLY STARTS, not from the height of the scroller.
        //
        // The scroller's height was the second wrong answer. It says how much room the PAGE has,
        // and the chart does not begin at the top of the page -- it begins under the field list,
        // the shelves, the filters and the chart's own controls. On a layout where those run long,
        // a chart sized to the whole scroller runs off the bottom, and the reader sees the top of
        // a drawing and a screen of white. Reported by Ed with a screenshot, which showed the apex
        // of a triangle and nothing else; no measurement I had taken said anything was wrong,
        // because I was measuring the page rather than the gap the chart had to fit into.
        //
        // SCROLLING IS NOT THE FIX EITHER. It helps when the chart is below the fold, and it does
        // nothing when the chart STARTS on screen and ends past the bottom.
        const measure = () => {
            const scroller = scrollerRef.current;
            if (!scroller) return;
            const box = chartRef.current;
            const room = box
                ? scroller.getBoundingClientRect().bottom - box.getBoundingClientRect().top - 40
                : scroller.clientHeight - 56;
            // Never so short it is useless, never so tall it is a poster, and never taller than
            // the space it has.
            setChartHeight(Math.min(CHART_MAX_HEIGHT, Math.max(CHART_MIN_HEIGHT, room)));
        };
        measure();
        // AGAIN AFTER LAYOUT SETTLES. The first pass runs before the chart controls above it have
        // their final height -- a radar declares three shelves where a bar declares two -- so the
        // gap measured first is not the gap that ends up there.
        const settle = window.setTimeout(measure, 150);
        window.addEventListener("resize", measure);
        return () => { window.clearTimeout(settle); window.removeEventListener("resize", measure); };
    }, [view, chart?.kind]);

    // BROUGHT INTO VIEW when the reader asks for a chart, and when they change which one -- both
    // are moments where the answer is the drawing and it is off screen.
    //
    // "nearest" RATHER THAN "start": already looking at it, a change of type must not jerk the
    // page. It scrolls only when there is something to scroll to, which is what makes this safe to
    // run on every change rather than only the first.
    React.useEffect(() => {
        if (view !== "chart") return;
        const handle = window.setTimeout(
            () => chartRef.current?.scrollIntoView({block: "nearest", behavior: "smooth"}), 80);
        return () => window.clearTimeout(handle);
    }, [view, chart?.kind]);

    const drawable = ran ? availableCharts(result.Columns, result.Rows) : [];
    const anyChart = drawable.some(one => !one.refusal);
    const views: {kind: string; label: string; refusal: {why: string} | null}[] = [
        ...(ran ? [{kind: "table", label: "Table", refusal: null},
                   {kind: "chart", label: "Chart",
                    refusal: anyChart ? null : (drawable[0]?.refusal ?? null)}] : []),
        ...(query ? [{kind: "query", label: "Query", refusal: null}] : []),
    ];

    // THE CHOSEN VIEW, FALLING BACK ONLY ONCE THERE IS A RESULT. A view that has stopped being
    // offered must not leave the panel blank, which is what reading `view` straight through would
    // do -- but before a run, NOTHING is shown until somebody asks for it. Defaulting to Query
    // there meant the page compiled the statement on the server after every shelf edit, unasked;
    // the e2e suite caught it as an Explain request in the middle of a test about running.
    const offered = views.some(one => one.kind === view);
    const showing = offered ? view : ran ? views[0]?.kind : undefined;

    return (
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 0,
                     height: "100%", backgroundColor: "#FFFFFF"}}>
            {/* THE BLUE BAR NAMES THE AREA and carries the persona readout, which is how Terms and
                Rules do it: two white lines, the mode and the Position it resolves to. Naming the
                persona and CHANGING it are two jobs, and those pages keep them apart -- the readout
                here, the tabs below. */}
            <PageHeader title="Reporting">
                <PersonaHeaderInfo/>
            </PageHeader>

            <div ref={scrollerRef} style={{flex: 1, overflow: "auto", padding: "20px 30px 40px"}}>
                {/* SCREEN HEADER: the blue bar names the area, this names the screen. Same two
                    levels as the Glossary and the Rules page, and the same 34px tinted tile so the
                    three read as one product rather than three. */}
                <div style={{display: "flex", flexDirection: "row", alignItems: "center",
                             justifyContent: "space-between", padding: "12px 0"}}>
                    <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: 12}}>
                        <Box sx={{width: 34, height: 34, borderRadius: "6px",
                                  backgroundColor: "rgba(20,152,216,0.12)", display: "flex",
                                  alignItems: "center", justifyContent: "center"}}>
                            <TableChartOutlined sx={{color: Blue3, fontSize: 20}}/>
                        </Box>
                        <Typography style={{fontSize: 20, fontWeight: 500, color: Grey17}}>Report builder</Typography>
                    </div>
                    {/* THE GUIDE, BESIDE THE SCREEN IT DESCRIBES, exactly where the Glossary and
                        the Rules page put theirs. It is also in the rail under Documentation --
                        both, because somebody stuck on this screen looks here, and somebody
                        wondering what documentation exists at all looks there. */}
                    <Tooltip title="Open the Reporting guide">
                        <IconButton aria-label="Open the Reporting guide"
                                    onClick={() => openGuide("reporting.html")}
                                    style={{color: Blue3}}>
                            <MenuBook/>
                        </IconButton>
                    </Tooltip>
                </div>

                {/* THE VIEW ROW, as on Terms and Rules: the persona tabs on the left, ruled off
                    from the content below. Anything that changes how the screen is READ rather than
                    what it contains belongs on this line. */}
                <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between",
                             alignItems: "flex-end", borderBottom: "1px solid #E4E7EC",
                             marginBottom: 20}}>
                    <PersonaScope/>
                </div>

                {failure ? (
                    <Typography sx={{fontSize: 13, color: "#B42318", mb: 2}}>{failure}</Typography>
                ) : null}

                <Box sx={{display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap"}}>
                    <TextField select size="small" label="Report on" sx={{width: BUILDER_COLUMN}}
                               value={shelves.dataset}
                               onChange={event => {
                                   // EVERY SHELF IS CLEARED WITH THE DATASET. The fields on them
                                   // belong to the old one; keeping them would leave a report that
                                   // cannot compile, displayed as though it could.
                                   // THE SHAPE SURVIVES A DATASET CHANGE. It is a statement about
                                   // what the reader wants to see, not about this dataset's fields.
                                   setShelves({...EMPTY, dataset: event.target.value,
                                               ...(shelves.shape ? {shape: shelves.shape} : {})});
                                   setRan(false);
                               }}>
                        {datasets.map(one => (
                            <MenuItem key={one.Name} value={one.Name}>{one.Name}</MenuItem>
                        ))}
                    </TextField>
                    {/* A PROPERTY OF THE REPORT, SO IT SITS WITH THE DATASET. It changes what
                        every shelf means at once, which is why it is not a control on a shelf --
                        somebody setting it per field would be describing two reports.

                        PHRASED AS THE QUESTION THE DRAWER ALREADY RAISES: a dimension "decides what
                        one row means". This is where that is decided. */}
                    <TextField select size="small" label="One row per" sx={{width: BUILDER_SECOND_COLUMN}}
                               value={shelves.shape ?? "summary"}
                               onChange={event => {
                                   const shape = event.target.value as "summary" | "detail";
                                   // MEASURES COME OFF WITH THE SWITCH. Leaving them on a listing
                                   // would be a report that cannot compile, displayed as though it
                                   // could -- the same reason changing dataset clears the shelves.
                                   setShelves(shape === "detail"
                                       ? {...shelves, shape, values: [],
                                          sort: (shelves.sort ?? []).filter(one => !one.aggregation),
                                          filters: shelves.filters.filter(
                                              one => !(one as any).afterGrouping)}
                                       : {...shelves, shape});
                                   setRan(false);
                               }}>
                        {SHAPES.filter(one => one.phase === "v1").map(one => (
                            <MenuItem key={one.id} value={one.id}>{one.label}</MenuItem>
                        ))}
                    </TextField>
                    {/* THE ICON SITS BESIDE THE CONTROL IT EXPLAINS. Same drawer the shelf
                        headings open, a third topic in it -- because "which fields do I see?" and
                        "why did my two records become one line?" are the same question. */}
                    <IconButton size="small" aria-label="What one row per means"
                                onClick={() => setShapeHelp(true)}
                                sx={{p: 0.25, color: Blue3, "&:hover": {color: Grey17}}}>
                        <HelpOutlineOutlined sx={{fontSize: 15, display: "block"}}/>
                    </IconButton>
                    <FieldRoleHelp role={shapeHelp ? "listing" : null}
                                   onClose={() => setShapeHelp(false)}/>
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>
                        {datasets.length} dataset{datasets.length === 1 ? "" : "s"} in this account
                    </Typography>
                </Box>

                <SavedReportBar reports={saved} current={current} name={name}
                                // A CHART SOMEBODY BUILT IS UNSAVED WORK TOO. Marking only the
                                // shelves would leave a reader who had spent five minutes on a
                                // chart looking at a report that says it is saved.
                                dirty={!!current && (JSON.stringify(shelves) !== savedShelves
                                    || (chart ? JSON.stringify(chart) : null) !== savedChart)}
                                runs={runs} busy={busy}
                                onNameChange={setName} onOpen={openSaved} onSave={keep}
                                onOpenRun={openRun}/>

                {/* BESIDE SAVING AND HISTORY, because that is what a schedule is: a report you
                    kept, run again later, landing in the same history. It renders nothing until
                    the report is saved -- a schedule references a definition rather than copying
                    one, so there is nothing to point at until one exists. */}
                <ScheduleBar reportUSID={current?.USID ?? null}/>

                <ReportShelves dataset={dataset} shelves={shelves} onChange={setShelves}/>

                <Box sx={{display: "flex", alignItems: "center", gap: 2, mt: 1, mb: 3, flexWrap: "wrap"}}>
                    <Button size="small" className={css_buttons.blueStandardButton}
                            disabled={busy || wrong.length > 0} onClick={run}>
                        {busy ? "Running…" : "Run report"}
                    </Button>
                    {wrong.length ? (
                        <Typography sx={{fontSize: 12.5, color: Grey6}}>
                            {wrong.map(one => `${one.where}: ${one.why}`).join(" · ")}
                        </Typography>
                    ) : null}
                </Box>

                {relevant.length ? (
                    <Box sx={{mb: 3}}>
                        <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                                         textTransform: "uppercase", color: Blue3, mb: 0.5}}>
                            About this dataset
                        </Typography>
                        {/* CARRIED FROM THE SERVER AND SHOWN. A field that is not reportable is a
                            fact somebody needs at the moment they look for it and cannot find it --
                            not one to leave in a log.

                            GROUPED, AND BY NAME. One line per note headed by the field's CODE made
                            this forty lines on a dataset that spans many forms, twenty-five of them
                            the same sentence about File fields -- a wall that reads as gibberish
                            and buries the one note worth acting on. See datasetNotes. */}
                        {groupNotes(relevant).map((line, index) => (
                            <Typography key={index} sx={{fontSize: 12.5, color: Grey6, mb: 0.25}}>
                                {noteText(line)}
                            </Typography>
                        ))}
                    </Box>
                ) : null}

                {views.length ? (
                    <Box sx={{minHeight: showing ? 260 : 0}}>
                        {/* THE TOGGLE OFFERS WHAT THIS RESULT SUPPORTS, and says why when it does
                            not. Switching to a chart a result cannot be drawn as is the classic way
                            to get a blank rectangle and no explanation.
                            QUERY IS ONE OF THESE, on the right. It is a view of this report like
                            the others -- and reading the statement against the numbers it produced
                            is why anybody opens it, which they cannot do from opposite ends of the
                            page. Before a run it is the only view there is. */}
                        <Box sx={{display: "flex", gap: 1, mb: 1.5, alignItems: "center",
                                  flexWrap: "wrap"}}>
                            {views.map(one => (
                                <Tooltip key={one.kind}
                                         title={one.refusal ? `This report ${one.refusal.why}.` : ""}>
                                    <Box onClick={() => { if (!one.refusal) setView(one.kind as any); }}
                                         sx={{px: 1.5, py: 0.5, borderRadius: "4px", fontSize: 13,
                                              border: "1px solid #E4E7EC",
                                              cursor: one.refusal ? "default" : "pointer",
                                              backgroundColor: showing === one.kind
                                                  ? "rgba(20,152,216,0.12)" : "transparent",
                                              color: one.refusal ? "#C3C7CF"
                                                  : showing === one.kind ? Blue3 : Grey6}}>
                                        {one.label}
                                    </Box>
                                </Tooltip>
                            ))}

                            {/* THE EXPORT, AND IT IS AN ORDINARY ONE. The rows are already on this
                                page -- they came back with the run -- so the file is built here
                                rather than from a server endpoint that would be a second place
                                serving tenant rows, with its own guard, persona and realm to get
                                right. Nothing is disclosed that was not already on screen.

                                IN THE READER'S OWN SESSION, under their own persona, which is what
                                makes this different from a scheduled export: they are present, and
                                they asked. */}
                            {result.Columns?.length ? (
                                <>
                                    <div style={{flexGrow: 1}}/>
                                    <Tooltip title="Save these rows as a CSV file">
                                        <Box role="button"
                                             aria-label="Download this result as CSV"
                                             onClick={() => downloadCsv(
                                                 csvFilename(current?.Name, openedRunAt),
                                                 resultToCsv(result.Columns, result.Rows))}
                                             sx={{display: "flex", alignItems: "center", gap: 0.5,
                                                  px: 1.5, py: 0.5, borderRadius: "4px",
                                                  fontSize: 13, color: Blue3, cursor: "pointer",
                                                  border: "1px solid #E4E7EC",
                                                  "&:hover": {backgroundColor: "#FAFBFC"}}}>
                                            <FileDownloadOutlined sx={{fontSize: 16}}/>
                                            CSV
                                        </Box>
                                    </Tooltip>

                                    {/* AND THE SPREADSHEET, which is a different promise from the
                                        CSV rather than a nicer one. Excel applies its own import
                                        rules to a .csv: a cell reading 1-2 opens as 2 January, 0042
                                        opens as 42, and one beginning = is a formula -- which is
                                        why the CSV above prefixes an apostrophe, visibly, in the
                                        cell. In a workbook the cell is typed, so the value is left
                                        exactly as the report returned it.

                                        BUILT BY THE SERVER FROM THE RECORDED RUN, so it needs one,
                                        which is also what makes it the right file to attach to
                                        something: the bytes and the evidence are the same rows. */}
                                    {/* THEY SIT BESIDE EACH OTHER rather than in a format picker.
                                        This note used to say "if a third arrives, this becomes a
                                        picker", and a third has: Avro. It stays buttons anyway,
                                        because the REASON was never the count -- it was that a
                                        menu hides the one people want behind a click, and that is
                                        as true of three as of two. The real trigger is the row
                                        getting crowded, and with CSV that is four short controls
                                        on a wide toolbar. Revisit when they wrap, not when they
                                        reach a number guessed in advance. */}
                                    {runUSID ? ([
                                        {format: "xlsx" as const, label: "Excel",
                                         hint: "Save this run as an Excel workbook"},
                                        {format: "parquet" as const, label: "Parquet",
                                         hint: "Save this run as a Parquet file, for loading into "
                                               + "something else"},
                                        {format: "avro" as const, label: "Avro",
                                         hint: "Save this run as an Avro container file, schema "
                                               + "included, for a pipeline that expects one"},
                                    ]).map(one => (
                                        <Tooltip key={one.format} title={one.hint}>
                                            <Box role="button"
                                                 aria-label={`Download this result as ${one.label}`}
                                                 onClick={() => {
                                                     setExporting(one.format);
                                                     exportRun(runUSID, one.format)
                                                         .then(saveExported)
                                                         .catch(() => setFailure(
                                                             "That run could not be exported."))
                                                         .finally(() => setExporting(null));
                                                 }}
                                                 sx={{display: "flex", alignItems: "center",
                                                      gap: 0.5, px: 1.5, py: 0.5,
                                                      borderRadius: "4px", fontSize: 13,
                                                      color: exporting ? Grey5 : Blue3,
                                                      cursor: exporting ? "default" : "pointer",
                                                      border: "1px solid #E4E7EC",
                                                      "&:hover": {backgroundColor: "#FAFBFC"}}}>
                                                <FileDownloadOutlined sx={{fontSize: 16}}/>
                                                {exporting === one.format ? "Building…" : one.label}
                                            </Box>
                                        </Tooltip>
                                    )) : null}
                                </>
                            ) : null}
                        </Box>

                        {/* NOTHING IS A VIEW TOO, and it has to be written down. Before a run the
                            only tab is Query and none is selected, and a chain of ternaries with no
                            branch for that falls off the end into the LAST one -- which rendered a
                            chart of no rows, empty and bordered, under a page that had not run
                            anything. */}
                        {!showing ? null
                            : showing === "query"
                            ? <ExplainPanel key={personaEpoch} query={query}/>
                            : showing === "table"
                            ? <ReportResultTableApplet enterpriseComponent={component}
                                                       resultColumns={result.Columns}
                                                       recordKeys={result.RecordKeys}/>
                            : (
                                <>
                                    <ChartBuilder spec={chart ?? defaultSpec(
                                                      drawable.find(one => !one.refusal)?.kind
                                                          ?? "bar", result.Columns)}
                                                  columns={result.Columns} rows={result.Rows}
                                                  onChange={setChart}/>
                                    {/* scrollMarginBottom IS THE PART THAT MATTERS, and the first
                                        attempt at this did not have it. `block: "nearest"` scrolls
                                        the MINIMUM needed, so it parked the chart's last pixel row
                                        on the window's last pixel row -- in view by any
                                        measurement, and indistinguishable from a drawing that has
                                        been cut off. scroll-margin is what "nearest" treats as the
                                        element's edge, so this is the breathing room, not the mb
                                        below it: margin moves what FOLLOWS, and nothing follows. */}
                                    <Box ref={chartRef} sx={{mb: 5, scrollMarginBottom: "56px"}}>
                                        <ReportChart spec={chart ?? defaultSpec(
                                                         drawable.find(one => !one.refusal)?.kind
                                                             ?? "bar", result.Columns)}
                                                     columns={result.Columns} rows={result.Rows}
                                                     height={chartHeight}/>
                                    </Box>
                                </>
                            )}
                    </Box>
                ) : null}
            </div>
        </div>
    );
}
